// La isla del telefono. Pastilla arriba (estado o llamada en curso) que se expande con un
// resorte al softphone completo y se encoge para seguir navegando sin cortar la llamada.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { buscarPersonas } from '../api/crm';
import { getSession } from '../api/http';
import { borrarCuentaVoz, configurarCuentaVoz, miCuentaVoz, reintentarRegistroVoz, TEXTO_ESTADO, type EstadoRegistro } from '../api/voz';
import { useSession } from '../auth/session';
import { accionPuente } from '../api/puente';
import { toast } from '../ui/toasts';
import { useLlamadasRecientes, useTipificaciones } from '../data/queries';
import { esMovil, useDebounced, useNow, usePhone } from '../hooks';
import { duration, initials, isToday, shortDate, time } from '../lib/format';
import { Icon, PhoneFilled, type IconName } from '../lib/icons';
import { dialable, formatPhone, KEYPAD, looksLikeNumber, searchKey } from '../lib/phone-number';
import { SpringValue } from '../lib/spring';
import { probarTimbre } from '../lib/audio';
import { notificacionesPosibles, pedirPermisoNotificaciones, permisoNotificaciones } from '../lib/avisoLlamada';
import { configurado, defaultWs, type ModoTelefono, type PhoneConfig } from './config';
import { Ensayo } from './Ensayo';
import { phone, PRESENCIAS, type Llamada, type Pestana, type Presencia } from './store';
import { ColasEnVivo, MiDesvio, useEstadoPuente } from '../ui/CentroContacto';

const dims = (modo: 'pill' | 'pillCall' | 'banner' | 'panel') => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Celular (D100): la pastilla va abajo a la izquierda (Amix a la derecha) y el telefono abierto sube como una hoja
  // a lo ancho, dejando libres las barras del sistema arriba y abajo.
  const mob = esMovil();
  const pw = mob ? Math.min(560, vw - 16) : Math.min(380, vw - 16);
  if (modo === 'pill') return [mob ? 200 : 236, 40, 20];
  if (modo === 'pillCall') return [Math.min(mob ? 270 : 300, vw - 24), 40, 20];
  if (modo === 'banner') return [pw, 204, 32];
  return [pw, mob ? Math.min(680, vh - 32) : Math.min(640, vh - 24), 30];
};

const PresDot = ({ p }: { p: Presencia }) => <span className={`idot ${p}`} aria-hidden />;

function IAvatar({ nombre, numero, className = '' }: { nombre?: string; numero: string; className?: string }) {
  return (
    <div className={`iav ${className}`} title={nombre ?? formatPhone(numero)}>
      {nombre ? initials(nombre) : <Icon name="phone" />}
    </div>
  );
}

function Timer({ desde }: { desde: number | null }) {
  const now = useNow(500, !!desde);
  return <>{desde ? duration((now - desde) / 1000) : '0:00'}</>;
}

export function Island() {
  const s = usePhone();
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const springs = useRef<{ w: SpringValue; h: SpringValue; r: SpringValue } | null>(null);
  const prevModo = useRef<string>('pill');
  const navigate = useNavigate();

  const modoVisual = s.isla === 'pill' ? (s.call ? 'pillCall' : 'pill') : s.isla;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!springs.current) {
      const [w, h, r] = dims(modoVisual);
      springs.current = {
        w: new SpringValue(w, (v) => (el.style.width = `${v}px`), 0.3),
        h: new SpringValue(h, (v) => (el.style.height = `${v}px`), 0.3),
        r: new SpringValue(r, (v) => (el.style.borderRadius = `${v}px`), 0.1),
      };
      springs.current.w.set(w);
      springs.current.h.set(h);
      springs.current.r.set(r);
    }
    const [w, h, r] = dims(modoVisual);
    if (panelRef.current && (modoVisual === 'panel' || modoVisual === 'banner')) {
      panelRef.current.style.width = `${w}px`;
      panelRef.current.style.height = `${h}px`;
    }
    const expandiendo = (modoVisual === 'panel' || modoVisual === 'banner') && !(prevModo.current === 'panel' || prevModo.current === 'banner');
    prevModo.current = modoVisual;
    // Crece con un poco de rebote (hay intencion, como un gesto); se encoge sin rebote.
    const o = expandiendo ? { damping: 0.86, response: 0.44 } : { damping: 1, response: 0.36 };
    springs.current.w.go(w, o);
    springs.current.h.go(h, o);
    springs.current.r.go(r, o);
  }, [modoVisual]);

  useEffect(() => {
    const onResize = () => {
      const sp = springs.current;
      if (!sp) return;
      const [w, h, r] = dims(prevModo.current as 'pill');
      sp.w.set(w);
      sp.h.set(h);
      sp.r.set(r);
      if (panelRef.current) {
        panelRef.current.style.width = `${w}px`;
        panelRef.current.style.height = `${h}px`;
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Clic fuera: la isla vuelve a ser pastilla (salvo que este sonando).
  useEffect(() => {
    if (s.isla !== 'panel') return;
    const fn = (e: PointerEvent) => {
      const t = e.target as HTMLElement;
      if (ref.current?.contains(t) || t.closest('[data-tel],.sheet,.sheet-scrim,.dialog,.scrim,.cmdk,.toast,[data-baliza-ui]')) return;
      phone.collapse();
    };
    document.addEventListener('pointerdown', fn);
    return () => document.removeEventListener('pointerdown', fn);
  }, [s.isla]);

  const abierto = s.isla !== 'pill';
  return (
    <div className="island" ref={ref} data-mode={modoVisual} data-testid="isla">
      <button className={`i-pill ${s.call?.espera ? 'hold' : ''}`} onClick={() => phone.expand()} inert={abierto || undefined} aria-label={s.call ? `Volver a la llamada con ${s.call.contacto?.nombre || formatPhone(s.call.numero)}` : `Abrir teléfono. ${pillLabel(s.lineStatus, s.presencia)}`}>
        <Pill />
      </button>
      <div className="i-panel" ref={panelRef} role="dialog" aria-label="Teléfono" inert={!abierto || undefined}>
        {s.call?.estado === 'sonando' ? <Incoming /> : s.call ? <CallView call={s.call} onFicha={(id) => navigate(`/personas/${id}`)} /> : <Idle />}
      </div>
    </div>
  );
}

function pillLabel(status: string, p: Presencia) {
  if (status === 'en-linea') return PRESENCIAS[p].nombre;
  if (status === 'conectando') return 'Conectando…';
  if (status === 'sin-configurar') return 'Configura el teléfono';
  return 'Sin conexión';
}

function Pill() {
  const s = usePhone();
  const c = s.call;
  if (!c) {
    const online = s.lineStatus === 'en-linea';
    const cls = online ? s.presencia : s.lineStatus === 'conectando' ? 'con' : s.lineStatus === 'sin-configurar' ? 'off' : 'err';
    return (
      <>
        <span className={`idot ${cls}`} aria-hidden />
        <span className="pn">{pillLabel(s.lineStatus, s.presencia)}</span>
        {s.config.extension ? <span className="x">Ext {s.config.extension}</span> : null}
        {s.config.modo === 'demo' ? <span className="x">Demo</span> : null}
        <span className="grow" />
        <span className="k">
          <Icon name="keypad" />
        </span>
      </>
    );
  }
  const nombre = c.contacto?.nombre || formatPhone(c.numero);
  const st = c.estado === 'marcando' ? 'Llamando' : c.estado === 'timbrando' ? 'Timbrando' : c.espera ? 'En espera' : '';
  return (
    <>
      <span className={`idot ${c.estado === 'activa' && !c.espera ? 'live' : ''}`} style={{ ['--c' as string]: c.espera ? 'var(--amber)' : 'var(--green)' }} aria-hidden />
      <span className="pn">{nombre}</span>
      <span className="grow" />
      {s.entrante2 ? <span className="badge">+1</span> : null}
      {st ? (
        <span className="x">{st}…</span>
      ) : (
        <>
          <span className="vbars" aria-hidden>
            <i />
            <i />
            <i />
            <i />
          </span>
          <span className="tmr">
            <Timer desde={c.inicio} />
          </span>
        </>
      )}
    </>
  );
}

/* ---------------- sin llamada: pestanas ---------------- */

const TABS: [Pestana, string, IconName][] = [
  ['teclado', 'Teclado', 'keypad'],
  ['recientes', 'Recientes', 'clock'],
  ['buzon', 'Buzón', 'voicemail'],
  ['estado', 'Estado', 'status'],
  ['ajustes', 'Ajustes', 'sliders'],
];

function Idle() {
  const s = usePhone();
  const { yo } = useSession();
  const perdidas = s.recientes.filter((r) => r.perdida && Date.now() - r.fecha < 86_400_000).length;
  const nombre = s.config.nombre || yo.nombre;
  const sub = s.lineStatus === 'en-linea' ? `${s.config.extension ? `Ext ${s.config.extension} · ` : ''}${PRESENCIAS[s.presencia].nombre}` : s.lineDetail || pillLabel(s.lineStatus, s.presencia);
  return (
    <>
      <div className="ph-h">
        <button className="i-ib clear" onClick={() => phone.setPestana('estado')} aria-label="Cambiar estado">
          <PresDot p={s.presencia} />
        </button>
        <div className="who">
          <b>{nombre}</b>
          <span>{sub}</span>
        </div>
        {s.config.modo === 'demo' ? (
          <button className="i-ib" onClick={() => phone.simularEntrante()} aria-label="Simular llamada entrante" title="Simular llamada entrante">
            <Icon name="phoneIn" />
          </button>
        ) : null}
        <button className="i-ib" onClick={() => phone.collapse()} aria-label="Minimizar teléfono">
          <Icon name="chevUp" />
        </button>
      </div>
      <div className="ph-body">
        {s.pestana === 'teclado' ? <Teclado /> : s.pestana === 'recientes' ? <Recientes /> : s.pestana === 'buzon' ? <Buzon /> : s.pestana === 'estado' ? <Estado /> : <Ajustes />}
      </div>
      <nav className="ph-tabs" aria-label="Secciones del teléfono">
        {TABS.map(([k, n, i]) => (
          <button key={k} onClick={() => phone.setPestana(k)} aria-pressed={s.pestana === k}>
            <Icon name={i} />
            {n}
            {k === 'recientes' && perdidas ? <span className="n">{perdidas}</span> : null}
          </button>
        ))}
      </nav>
    </>
  );
}

function Teclado() {
  const s = usePhone();
  const q = useDebounced(s.marcador, 250);
  const [sug, setSug] = useState<{ id: string; nombre: string; empresa?: string; numero: string } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let vivo = true;
    const t = q.trim();
    setSug(null);
    if (t.length < 2) return;
    if (looksLikeNumber(t)) {
      if (searchKey(t).length >= 7) phone.buscar(t).then((c) => vivo && c && setSug({ ...c, numero: t }));
      return;
    }
    buscarPersonas(t, 1)
      .then((p) => {
        const x = p.items[0];
        if (vivo && x && x.telefono) setSug({ id: x.id, nombre: x.nombreCompleto, empresa: x.empresa?.nombre, numero: x.telefono });
      })
      .catch(() => {});
    return () => {
      vivo = false;
    };
  }, [q]);

  useEffect(() => {
    if (!esMovil()) inputRef.current?.focus({ preventScroll: true });
  }, []);

  const puedeLlamar = s.lineStatus === 'en-linea' && (!!dialable(s.marcador) || !!sug);
  const llamar = () => {
    if (sug && !looksLikeNumber(s.marcador)) void phone.dial(sug.numero, { id: sug.id, nombre: sug.nombre, empresa: sug.empresa });
    else void phone.dial(s.marcador, sug ? { id: sug.id, nombre: sug.nombre, empresa: sug.empresa } : null);
  };

  return (
    <>
      <div className="dial-d">
        <input
          ref={inputRef}
          className="dial-in"
          inputMode="tel"
          autoComplete="off"
          placeholder="Nombre o número"
          value={s.marcador}
          onChange={(e) => phone.setMarcador(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              llamar();
            }
            if (e.key === 'Escape') phone.collapse();
          }}
          aria-label="Número o nombre a marcar"
          data-testid="marcador"
        />
        <div className="sugg-w">
          {sug ? (
            <button className="sugg" onClick={() => phone.dial(sug.numero, { id: sug.id, nombre: sug.nombre, empresa: sug.empresa })}>
              <span className="iav">{initials(sug.nombre)}</span>
              <b>{sug.nombre}</b>
              {sug.empresa ? <span>{sug.empresa}</span> : null}
            </button>
          ) : null}
        </div>
      </div>
      <div className="pad">
        {KEYPAD.map(([k, l]) => (
          <button key={k} className="key" onClick={() => phone.tecla(k)} aria-label={k}>
            <b>{k}</b>
            <small>{l}</small>
          </button>
        ))}
      </div>
      <div className="pad-act">
        <span />
        <button className="callbtn" onClick={llamar} disabled={!puedeLlamar && s.lineStatus === 'en-linea'} aria-label="Llamar" data-testid="llamar">
          <PhoneFilled size={26} />
        </button>
        <button className="i-ib clear" onClick={() => phone.borrar()} aria-label="Borrar" style={{ visibility: s.marcador ? 'visible' : 'hidden' }}>
          <Icon name="backspace" />
        </button>
      </div>
      {s.lineStatus !== 'en-linea' ? (
        <p className="i-note" style={{ textAlign: 'center' }}>
          {s.lineDetail || 'El teléfono no está en línea.'}{' '}
          <button className="i-link" onClick={() => phone.setPestana('ajustes')}>
            Abrir ajustes
          </button>
        </p>
      ) : null}
    </>
  );
}

type FilaReciente = { key: string; numero: string; nombre: string; detalle: string; fecha: number; perdida: boolean; entrante: boolean; personaId?: string | null };

function Recientes() {
  const s = usePhone();
  const llamadas = useLlamadasRecientes();
  const navigate = useNavigate();
  const ext = s.config.extension;

  const filas: FilaReciente[] = useMemo(() => {
    const delServidor =
      ext && s.config.modo !== 'demo'
        ? (llamadas.data ?? [])
            .filter((l) => l.agente === ext)
            .slice(0, 40)
            .map<FilaReciente>((l) => ({
              key: l.id,
              numero: l.numero,
              nombre: l.persona?.nombre || formatPhone(l.numero),
              detalle: [l.empresa?.nombre, l.tipificacion?.nombre ?? (l.estado === 'CONTESTADA' ? (l.direccion === 'ENTRANTE' ? 'Entrante' : 'Saliente') : l.estado === 'PERDIDA' ? 'Perdida' : 'No contestada'), l.duracion ? duration(l.duracion) : ''].filter(Boolean).join(' · '),
              fecha: new Date(l.inicio ?? l.creado).getTime(),
              perdida: l.direccion === 'ENTRANTE' && l.estado !== 'CONTESTADA',
              entrante: l.direccion === 'ENTRANTE',
              personaId: l.personaId,
            }))
        : [];
    if (delServidor.length) return delServidor;
    return s.recientes.map((r) => ({
      key: r.local,
      numero: r.numero,
      nombre: r.nombre || formatPhone(r.numero),
      detalle: [r.empresa, r.tipificacion ?? (r.perdida ? 'Perdida' : r.dir === 'in' ? 'Entrante' : 'Saliente'), r.duracion ? duration(r.duracion) : ''].filter(Boolean).join(' · '),
      fecha: r.fecha,
      perdida: r.perdida,
      entrante: r.dir === 'in',
    }));
  }, [llamadas.data, s.recientes, ext, s.config.modo]);

  if (!filas.length)
    return <p className="i-note" style={{ textAlign: 'center', marginTop: 40 }}>Aún no hay llamadas. {ext ? '' : 'Configura tu extensión en Ajustes para ver tu historial del CRM desde cualquier equipo.'}</p>;

  const hoy = filas.filter((f) => isToday(f.fecha));
  const antes = filas.filter((f) => !isToday(f.fecha));
  const grupo = (titulo: string, lista: FilaReciente[]) =>
    lista.length ? (
      <div key={titulo}>
        <div className="i-sec">{titulo}</div>
        {lista.map((f) => (
          <div className="i-row" key={f.key}>
            <span className={`idir ${f.perdida ? 'miss' : ''}`}>
              <Icon name={f.perdida ? 'phoneMiss' : f.entrante ? 'phoneIn' : 'phoneOut'} />
            </span>
            <button className="rt" style={{ textAlign: 'left' }} onClick={() => f.personaId && navigate(`/personas/${f.personaId}`)} disabled={!f.personaId}>
              <b className={f.perdida ? 'miss' : ''}>{f.nombre}</b>
              <span>{f.detalle}</span>
            </button>
            <span className="i-time">{titulo === 'Hoy' ? time(f.fecha) : shortDate(f.fecha)}</span>
            <button className="i-ib" onClick={() => phone.dial(f.numero)} aria-label={`Llamar a ${f.nombre}`}>
              <Icon name="phone" />
            </button>
          </div>
        ))}
      </div>
    ) : null;
  return (
    <>
      {grupo('Hoy', hoy)}
      {grupo('Anteriores', antes)}
    </>
  );
}

function Buzon() {
  return (
    <>
      <div className="i-sec">Buzón de voz</div>
      <div className="i-row" style={{ alignItems: 'flex-start', borderBottom: 0 }}>
        <span className="idir">
          <Icon name="voicemail" />
        </span>
        <div className="rt">
          <b>Llega con la integración XAPI de 3CX</b>
          <span style={{ whiteSpace: 'normal' }}>
            Aquí escucharás tus mensajes, ligados a la ficha de cada cliente, y los marcarás como atendidos. Mientras tanto, 3CX te los envía a tu correo.
          </span>
        </div>
      </div>
      <p className="i-note">Seguimiento en el tablero del proyecto: “Buzón de voz en el CRM”.</p>
    </>
  );
}

function Estado() {
  const s = usePhone();
  const puente = useEstadoPuente();
  return (
    <>
      <div className="i-sec">Tu estado</div>
      {(Object.keys(PRESENCIAS) as Presencia[]).map((k) => (
        <button key={k} className="ipres" role="radio" aria-checked={s.presencia === k} onClick={() => phone.setPresencia(k)}>
          <PresDot p={k} />
          <div className="rt">
            <b>{PRESENCIAS[k].nombre}</b>
            <span>{PRESENCIAS[k].detalle}</span>
          </div>
          <span className="ok">
            <Icon name="check" />
          </span>
        </button>
      ))}
      <p className="i-note">
        {puente.data?.registrado
          ? 'Tu estado se sincroniza con 3CX (app, teléfono de escritorio y cliente web) por el Puente XmarteK.'
          : 'Tu estado vive en el CRM. Para verlo también en 3CX, tu administrador instala el Puente XmarteK (Administración → Integración 3CX).'}
      </p>
      {puente.data?.registrado ? (
        <>
          <div className="i-sec">Mis colas</div>
          <ColasEnVivo soloMias />
          <div className="i-sec">Desvío</div>
          <MiDesvio />
        </>
      ) : null}
    </>
  );
}

// Aviso de Windows cuando entra una llamada y la Consola esta en otra pestaña o minimizada (D68).
function AvisosDeLlamada() {
  const [permiso, setPermiso] = useState(permisoNotificaciones());
  if (!notificacionesPosibles()) return null;
  return (
    <div className="i-row">
      <div className="rt">
        <b>Aviso de Windows al entrar una llamada</b>
        <span>
          {permiso === 'granted'
            ? 'Activado: te avisa aunque estés en otra pestaña'
            : permiso === 'denied'
              ? 'Bloqueado en este navegador: actívalo en el candado junto a la dirección'
              : 'Te avisa aunque estés en otra pestaña'}
        </span>
      </div>
      {permiso === 'default' ? (
        <button className="i-btnw" type="button" style={{ width: 'auto', height: 36, padding: '0 14px' }} onClick={() => void pedirPermisoNotificaciones().then(setPermiso)}>
          Permitir
        </button>
      ) : null}
    </div>
  );
}

// Quitar la extension del CRM (D67): des-registra la pasarela de 3CX y borra su cuenta. Antes no habia forma desde la
// Consola y el registro quedaba vivo en el servidor aunque se quitara del lado de 3CX.
function QuitarExtension({ alQuitar }: { alQuitar: () => void }) {
  const [confirmar, setConfirmar] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');
  const quitar = async () => {
    setOcupado(true);
    setError('');
    try {
      await borrarCuentaVoz();
      phone.disconnect();
      phone.saveConfig({ ...phone.getState().config, usuario: '', clave: '', extension: '' });
      alQuitar();
      setConfirmar(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setOcupado(false);
    }
  };
  if (!confirmar)
    return (
      <button className="i-btnw" type="button" onClick={() => setConfirmar(true)}>
        Quitar mi extensión del CRM
      </button>
    );
  return (
    <div className="i-note" role="group" aria-label="Confirmar">
      <p style={{ margin: '0 0 8px' }}>El CRM deja de registrarse en 3CX con tu extensión. Tu app y tu teléfono de 3CX siguen igual.</p>
      <span style={{ display: 'flex', gap: 8 }}>
        <button className="i-btnw" type="button" disabled={ocupado} onClick={() => void quitar()}>
          {ocupado ? 'Quitando…' : 'Sí, quitarla'}
        </button>
        <button className="i-btnw" type="button" onClick={() => setConfirmar(false)}>
          No
        </button>
      </span>
      {error ? <p className="i-note err">{error}</p> : null}
    </div>
  );
}

function Ajustes() {
  const s = usePhone();
  const [c, setC] = useState<PhoneConfig>(s.config);
  const [verClave, setVerClave] = useState(false);
  // Prueba de audio y conexion (Ensayo): reemplaza los ajustes mientras esta abierta.
  const [ensayando, setEnsayando] = useState(false);
  const cerrarEnsayo = useCallback(() => setEnsayando(false), []);
  // Datos de la extension en 3CX: van al servidor, nunca se guardan en este navegador.
  const [x3cx, setX3cx] = useState({ fqdn: '', authId: '', clave: '' });
  const [cuenta, setCuenta] = useState<{ estado?: EstadoRegistro; error?: string; cargando?: boolean }>({});
  useEffect(() => setC(s.config), [s.config]);
  const set = <K extends keyof PhoneConfig>(k: K, v: PhoneConfig[K]) => setC((x) => ({ ...x, [k]: v }));
  const conUsuario = getSession()?.modo === 'usuario';

  // Al abrir: si el servidor ya tiene la cuenta de este agente, se completan los datos y el telefono queda listo.
  useEffect(() => {
    if (c.modo !== 'web' || !conUsuario) return;
    let vivo = true;
    miCuentaVoz()
      .then((r) => {
        if (!vivo || !r.configurado) return;
        setX3cx((v) => ({ ...v, fqdn: r.fqdn, authId: r.authId }));
        setCuenta({ estado: r.estado });
        const cfg = phone.getState().config;
        if (cfg.usuario !== r.usuario || cfg.clave !== r.clave || cfg.extension !== r.extension)
          phone.saveConfig({ ...cfg, modo: 'web', ws: defaultWs(), usuario: r.usuario, clave: r.clave, extension: r.extension });
      })
      .catch(() => {});
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.modo, conUsuario]);

  const guardar = async () => {
    // Dentro del clic: el navegador solo deja pedir el permiso de avisos con una accion de la persona (D68).
    void pedirPermisoNotificaciones();
    const base = { ...c, ws: c.ws.trim(), usuario: c.usuario.trim(), extension: c.extension.replace(/\D/g, ''), nombre: c.nombre.trim() };
    // Sin contrasena nueva pero con cuenta que 3CX no acepta: reintentar el registro (p. ej. tras desbloquear la IP).
    if (base.modo === 'web' && !x3cx.clave && cuenta.estado && cuenta.estado !== 'REGISTRADO') {
      setCuenta({ cargando: true });
      try {
        const r = await reintentarRegistroVoz();
        setCuenta({ estado: r.estado });
      } catch (e) {
        setCuenta({ error: (e as Error).message });
      }
      phone.saveConfig(base);
      return;
    }
    if (base.modo !== 'web' || !x3cx.clave) return phone.saveConfig(base);
    setCuenta({ cargando: true });
    try {
      const r = await configurarCuentaVoz({ fqdn: x3cx.fqdn.trim(), extension: base.extension, authId: x3cx.authId.trim(), clave: x3cx.clave });
      if (!r.configurado) throw new Error('No se pudo configurar');
      setX3cx((v) => ({ ...v, clave: '' }));
      setCuenta({ estado: r.estado });
      phone.saveConfig({ ...base, ws: defaultWs(), usuario: r.usuario, clave: r.clave, extension: r.extension });
    } catch (e) {
      setCuenta({ error: (e as Error).message });
    }
  };
  const online = s.lineStatus === 'en-linea';
  const reintentable = c.modo === 'web' && !!cuenta.estado && cuenta.estado !== 'REGISTRADO';
  const cambios = JSON.stringify(c) !== JSON.stringify(s.config) || !!x3cx.clave || reintentable;
  const modos: [ModoTelefono, string][] = [
    ['web', 'Teléfono web'],
    ['3cx', 'App 3CX'],
    ['demo', 'Demostración'],
  ];
  const textoBoton = reintentable && !x3cx.clave ? 'Reintentar con 3CX' : cambios ? 'Guardar y conectar' : online ? 'Conectado' : 'Conectar';
  if (ensayando) return <Ensayo onCerrar={cerrarEnsayo} onConectar={() => (cambios || !online ? void guardar() : undefined)} textoConectar={cambios || !online ? textoBoton : 'Listo'} />;
  return (
    <>
      <div className="i-row" style={{ borderBottom: 0, paddingTop: 4 }}>
        <span className={`idot ${online ? 'disponible' : s.lineStatus === 'conectando' ? 'con' : s.lineStatus === 'sin-configurar' ? 'off' : 'err'}`} />
        <div className="rt">
          <b>{online ? (s.config.modo === 'web' ? 'En línea · registrado en 3CX' : s.config.modo === '3cx' ? 'Clic para llamar activo' : 'Modo demostración') : pillLabel(s.lineStatus, s.presencia)}</b>
          <span style={{ whiteSpace: 'normal' }}>{s.lineDetail || (s.config.modo === 'web' ? 'Pasarela de voz → 3CX' : '')}</span>
        </div>
      </div>
      <label className="i-lbl">Cómo llamas</label>
      <div className="i-chips" role="radiogroup" aria-label="Modo del teléfono">
        {modos.map(([m, n]) => (
          <button key={m} className="i-chip" role="radio" aria-checked={c.modo === m} aria-pressed={c.modo === m} onClick={() => set('modo', m)}>
            {n}
          </button>
        ))}
      </div>
      <p className="i-note" style={{ marginTop: 8 }}>
        {c.modo === 'web'
          ? 'Hablas por el navegador con audífonos: la pasarela te registra como tu extensión de 3CX.'
          : c.modo === '3cx'
            ? 'Al marcar, 3CX hace sonar primero tu teléfono o app 3CX y luego conecta al cliente (plan Contact Center).'
            : 'Llamadas simuladas para capacitar o mostrar la Consola. No se guardan en el CRM.'}
      </p>
      <button className="i-row ens-entrada" type="button" onClick={() => setEnsayando(true)} data-baliza="ensayo">
        <span className="ens-entrada-ico" aria-hidden>
          <Icon name="headset" />
        </span>
        <span className="rt">
          <b>Probar audio y conexión</b>
          <span>{s.config.ensayoHecho ? `Última prueba: ${shortDate(s.config.ensayoHecho)}` : 'Micrófono, altavoces y central en 1 minuto'}</span>
        </span>
        <Icon name="chevRight" size={16} />
      </button>
      {c.modo === 'web' ? (
        <>
          <p className="i-note" style={{ marginTop: 4 }}>Usa los datos de tu extensión en 3CX (Admin → Usuarios → tu extensión → Teléfono IP / credenciales SIP).</p>
          <p className="i-note" style={{ marginTop: 4 }}>
            Importante: en la app web de 3CX ve a Settings → Audio/Video → «Use this device for calls» y deja <b>Browser</b> (no «Xmartek Softphone»). Si 3CX queda usando el CRM, no podrás contestar en 3CX cuando el CRM esté cerrado.
          </p>
          <label className="i-lbl" htmlFor="s3cx">Central 3CX</label>
          <input className="i-field mono" id="s3cx" value={x3cx.fqdn} onChange={(e) => setX3cx((v) => ({ ...v, fqdn: e.target.value }))} placeholder="tuempresa.3cx.lat" name="central-3cx" autoComplete="off" data-1p-ignore data-lpignore="true" spellCheck={false} />
          <label className="i-lbl" htmlFor="sAuth">Auth ID</label>
          <input className="i-field mono" id="sAuth" value={x3cx.authId} onChange={(e) => setX3cx((v) => ({ ...v, authId: e.target.value }))} placeholder="El de tu extensión en 3CX" name="auth-id-3cx" autoComplete="off" data-1p-ignore data-lpignore="true" spellCheck={false} />
          <label className="i-lbl" htmlFor="sPass">Contraseña de la extensión</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="i-field" id="sPass" type={verClave ? 'text' : 'password'} value={x3cx.clave} onChange={(e) => setX3cx((v) => ({ ...v, clave: e.target.value }))} placeholder={cuenta.estado ? 'Déjala vacía si no cambia' : ''} name="clave-extension-3cx" autoComplete="new-password" data-1p-ignore data-lpignore="true" />
            <button className="i-ib" style={{ width: 42, height: 42 }} onClick={() => setVerClave((v) => !v)} aria-label={verClave ? 'Ocultar contraseña' : 'Ver contraseña'}>
              <Icon name="eye" />
            </button>
          </div>
          {!conUsuario ? <p className="i-note err">Para configurar el teléfono web entra con tu usuario (no con API key).</p> : null}
          {cuenta.cargando ? <p className="i-note">Conectando con tu 3CX…</p> : null}
          {cuenta.estado ? <p className={`i-note ${cuenta.estado === 'REGISTRADO' ? 'ok' : cuenta.estado === 'RECHAZADO' ? 'err' : ''}`}>{TEXTO_ESTADO[cuenta.estado]}</p> : null}
          {cuenta.error ? <p className="i-note err">{cuenta.error}</p> : null}
          <details className="i-avanzado">
            <summary>Detalles técnicos</summary>
            <label className="i-lbl" htmlFor="sGw">Pasarela de voz</label>
            <input className="i-field mono" id="sGw" value={c.ws} onChange={(e) => set('ws', e.target.value)} autoComplete="off" />
            <label className="i-lbl" htmlFor="sUser">Usuario en la pasarela</label>
            <input className="i-field mono" id="sUser" value={c.usuario} onChange={(e) => set('usuario', e.target.value)} name="sip-usuario-pasarela" autoComplete="off" data-1p-ignore data-lpignore="true" spellCheck={false} />
            <label className="i-lbl" htmlFor="sPassWeb">Clave en la pasarela</label>
            <input className="i-field" id="sPassWeb" type="password" value={c.clave} onChange={(e) => set('clave', e.target.value)} name="sip-clave-pasarela" autoComplete="new-password" data-1p-ignore data-lpignore="true" />
          </details>
        </>
      ) : null}
      {c.modo !== 'demo' ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 10 }}>
          <div>
            <label className="i-lbl" htmlFor="sExt">Extensión 3CX</label>
            <input className="i-field mono" id="sExt" value={c.extension} onChange={(e) => set('extension', e.target.value)} inputMode="numeric" placeholder="104" />
          </div>
          <div>
            <label className="i-lbl" htmlFor="sName">Nombre del agente</label>
            <input className="i-field" id="sName" value={c.nombre} onChange={(e) => set('nombre', e.target.value)} placeholder="Tu nombre" />
          </div>
        </div>
      ) : null}
      <div className="i-row" style={{ marginTop: 12 }}>
        <div className="rt">
          <b>Sonar timbre</b>
          <span>Tono al entrar una llamada</span>
        </div>
        <button className="isw" role="switch" aria-checked={c.sonarTimbre} onClick={() => set('sonarTimbre', !c.sonarTimbre)} aria-label="Sonar timbre" />
      </div>
      <div className="i-row">
        <div className="rt">
          <b>Volumen del timbre</b>
          <span>Pulsa Probar para escucharlo</span>
        </div>
        <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            id="sVolumen"
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={s.config.volumenTimbre}
            onChange={(e) => phone.saveConfig({ ...phone.getState().config, volumenTimbre: Number(e.target.value) })}
            aria-label="Volumen del timbre"
            style={{ width: 110 }}
          />
          <button className="i-btnw" type="button" style={{ width: 'auto', height: 36, padding: '0 14px' }} onClick={probarTimbre}>
            Probar
          </button>
        </span>
      </div>
      <AvisosDeLlamada />
      <div className="i-row">
        <div className="rt">
          <b>Abrir la ficha del cliente</b>
          <span>Te lleva a su información automáticamente</span>
        </div>
        <select className="i-field" style={{ width: 132, height: 36 }} value={c.abrirFicha} onChange={(e) => set('abrirFicha', e.target.value as PhoneConfig['abrirFicha'])} aria-label="Cuándo abrir la ficha">
          <option value="sonar">Al sonar</option>
          <option value="contestar">Al contestar</option>
          <option value="nunca">Nunca</option>
        </select>
      </div>
      <div className="i-row">
        <div className="rt">
          <b>Tipificar llamadas internas</b>
          <span>Entre extensiones del equipo</span>
        </div>
        <select className="i-field" id="sInternas" style={{ width: 132, height: 36 }} value={s.config.tipificarInternas} onChange={(e) => phone.saveConfig({ ...phone.getState().config, tipificarInternas: e.target.value as PhoneConfig['tipificarInternas'] })} aria-label="Cuándo tipificar llamadas internas">
          <option value="opcional">Solo si quiero</option>
          <option value="siempre">Siempre</option>
        </select>
      </div>
      {!s.config.ensayoHecho && c.modo !== '3cx' ? (
        <p className="i-note ens-sugiere">
          ¿Primera vez? Antes de conectar,{' '}
          <button className="i-link" type="button" onClick={() => setEnsayando(true)}>
            prueba tu audio
          </button>{' '}
          para llamar sin sorpresas.
        </p>
      ) : null}
      <div className="btn-col">
        <button className="i-btnw acc" onClick={guardar} disabled={!cambios && online}>
          {textoBoton}
        </button>
        {online && c.modo === 'web' && !cambios ? (
          <button className="i-btnw" onClick={() => phone.disconnect()}>
            Desconectar
          </button>
        ) : null}
        {c.modo === 'web' && cuenta.estado ? <QuitarExtension alQuitar={() => setCuenta({})} /> : null}
      </div>
      <p className="i-note">
        Las llamadas siguen vivas mientras navegas por la Consola y los números del CRM se marcan desde aquí. Solo una llamada activa a la vez; si entra otra mientras hablas, puedes contestarla dejando la primera en espera.
      </p>
      {!configurado(c) && c.modo === 'web' && !x3cx.clave ? <p className="i-note">Llena los datos de tu extensión de 3CX y pulsa Conectar: la pasarela se configura sola.</p> : null}
    </>
  );
}

/* ---------------- sonando ---------------- */

function Incoming() {
  const s = usePhone();
  const c = s.call!;
  const navigate = useNavigate();
  const nombre = c.contacto?.nombre || 'Número desconocido';
  return (
    <>
      <div className="inc">
        <div className="av-r">
          <IAvatar nombre={c.contacto?.nombre} numero={c.numero} />
        </div>
        <div className="rt">
          <span className="cap">Llamada entrante{s.config.extension ? ` · Ext ${s.config.extension}` : ''}</span>
          <b>{nombre}</b>
          <span>
            {c.contacto?.empresa ? `${c.contacto.empresa} · ` : ''}
            <span className="mono">{formatPhone(c.numero)}</span>
          </span>
          {c.contacto ? (
            <button className="i-link" style={{ textAlign: 'left', marginTop: 4 }} onClick={() => navigate(`/personas/${c.contacto!.id}`)}>
              Ver ficha
            </button>
          ) : null}
        </div>
      </div>
      <div className="inc-b">
        <button className="rej-b" onClick={() => phone.reject()} data-testid="rechazar">
          <PhoneFilled size={18} hang /> Rechazar <kbd>R</kbd>
        </button>
        <button className="acc-b" onClick={() => phone.answer()} data-testid="contestar">
          <PhoneFilled size={18} /> Contestar <kbd>A</kbd>
        </button>
      </div>
    </>
  );
}

/* ---------------- en llamada ---------------- */

function Ctl({ act, icon, label, pressed, disabled, kbd, cls, title }: { act: () => void; icon: IconName; label: string; pressed?: boolean; disabled?: boolean; kbd?: string; cls?: string; title?: string }) {
  return (
    <button className={cls} onClick={act} aria-pressed={!!pressed} disabled={disabled} title={title}>
      <span className="c">
        <Icon name={icon} />
      </span>
      {label}
      {kbd ? <kbd>{kbd}</kbd> : null}
    </button>
  );
}

function CallView({ call: c, onFicha }: { call: Llamada; onFicha: (id: string) => void }) {
  const s = usePhone();
  const tips = useTipificaciones();
  const { tieneModulo } = useSession();
  const act = c.estado === 'activa';
  const nombre = c.contacto?.nombre || formatPhone(c.numero);
  const empresa = c.contacto?.empresa || (c.contacto ? '' : 'Número sin ficha');
  const [destino, setDestino] = useState('');
  // D80: pegar el caso a la llamada (Puente XmarteK) para que quien la reciba en 3CX lo vea.
  const puenteLlamada = useEstadoPuente();
  const [caso, setCaso] = useState('');
  const [casoPegado, setCasoPegado] = useState<string | null>(null);
  const pegarCaso = async () => {
    const n = caso.trim();
    if (!n) return;
    try {
      await accionPuente('datos', { datos: { caso: n, contacto: c.contacto?.id ?? '' } });
      setCasoPegado(n);
      toast(`Caso ${n} pegado: quien reciba la llamada lo verá.`);
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  };

  const statusTxt = c.estado === 'marcando' ? 'Llamando…' : c.estado === 'timbrando' ? 'Timbrando…' : c.consulta ? 'Consultando transferencia' : c.espera ? 'En espera' : `En llamada${c.silencio ? ' · silenciado' : ''}`;
  const tipNombre = tips.data?.find((t) => t.id === c.tipificacionId)?.nombre;
  const tipRequiere = tips.data?.find((t) => t.id === c.tipificacionId)?.requiereSeguimiento;

  const compact = (
    <div className="compact">
      <IAvatar nombre={c.contacto?.nombre} numero={c.numero} />
      <div className="rt">
        <b>{nombre}</b>
        <span>
          {empresa ? `${empresa} · ` : ''}
          {act ? <Timer desde={c.inicio} /> : formatPhone(c.numero)}
        </span>
      </div>
    </div>
  );
  const back = (t: string) => (
    <div className="sub-h">
      <button className="i-ib" onClick={() => phone.setVista('main')} aria-label="Volver a la llamada">
        <Icon name="chevLeft" />
      </button>
      <b>{t}</b>
    </div>
  );

  let mid: React.ReactNode = null;
  if (c.vista === 'main') {
    mid = (
      <>
        <div className="call-top">
          <IAvatar nombre={c.contacto?.nombre} numero={c.numero} className="call-av" />
          <div className="call-n" data-testid="llamada-nombre">{nombre}</div>
          <div className="call-c">{empresa}</div>
          <div className="call-t">{act ? <Timer desde={c.inicio} /> : <span className="mono">{formatPhone(c.numero)}</span>}</div>
          {tipNombre || c.notas ? (
            <div className="call-tags">
              {tipNombre ? <span className="i-chip" aria-pressed="true">{tipNombre}</span> : null}
              {c.notas ? <span className="i-chip">Con notas</span> : null}
            </div>
          ) : null}
        </div>
        <div className="ctl">
          <Ctl act={() => phone.toggleMute()} icon="micOff" label="Silencio" pressed={c.silencio} disabled={!act} kbd="M" />
          <Ctl act={() => phone.setVista('keypad')} icon="keypad" label="Teclado" disabled={!act} kbd="K" />
          <Ctl act={() => phone.toggleHold()} icon="pause" label="Espera" pressed={c.espera} disabled={!act || !!c.consulta} kbd="H" />
          <Ctl act={() => phone.setVista('transfer')} icon="transfer" label="Transferir" disabled={!act || s.config.modo === '3cx'} kbd="T" />
          <Ctl act={() => phone.setVista('conf')} icon="conf" label="Conferencia" disabled={!act} title="Llega en la fase 2" />
          <Ctl act={() => {}} icon="record" label="Grabar" disabled cls="recb" title="Grabación bajo demanda: fase 2 del mapa 3CX" />
          <Ctl act={() => phone.setVista('tip')} icon="tag" label="Tipificar" pressed={!!c.tipificacionId} disabled={!tieneModulo('TIPIFICACION')} />
          <Ctl act={() => phone.setVista('notes')} icon="note" label="Notas" pressed={!!c.notas} />
          <Ctl act={() => c.contacto && onFicha(c.contacto.id)} icon="user" label="Ficha" disabled={!c.contacto} kbd="F" />
        </div>
      </>
    );
  } else if (c.vista === 'keypad') {
    mid = (
      <>
        {compact}
        {back('Teclado')}
        <div className="dial-in" style={{ minHeight: 40, fontSize: 24 }} aria-live="polite">
          {c.dtmf || <span style={{ color: 'var(--i-3)', fontFamily: 'var(--sans)', fontSize: 15 }}>Tonos para menús (marque 1 para…)</span>}
        </div>
        <div className="pad">
          {KEYPAD.map(([k]) => (
            <button key={k} className="key" onClick={() => phone.dtmf(k)} aria-label={k}>
              <b>{k}</b>
            </button>
          ))}
        </div>
      </>
    );
  } else if (c.vista === 'transfer') {
    if (c.consulta) {
      const up = c.consulta.estado === 'activa';
      mid = (
        <>
          {compact}
          {back('Transferencia con consulta')}
          <div className="party">
            <IAvatar nombre={c.contacto?.nombre} numero={c.numero} />
            <div className="rt">
              <b>{nombre}</b>
              <span>En espera</span>
            </div>
            <span className="idot" style={{ ['--c' as string]: 'var(--amber)' }} />
          </div>
          <div className="party">
            <IAvatar nombre={c.consulta.nombre} numero={c.consulta.numero} />
            <div className="rt">
              <b>{c.consulta.nombre}</b>
              <span>{up ? 'Conectado · hablas en privado' : 'Timbrando…'}</span>
            </div>
            <span className={`idot ${up ? 'live' : ''}`} style={{ ['--c' as string]: up ? 'var(--green)' : 'var(--amber)' }} />
          </div>
          <div className="btn-col">
            <button className="i-btnw go" onClick={() => phone.completeConsult()} disabled={!up}>
              <Icon name="transfer" /> Completar transferencia
            </button>
            <button className="i-btnw" disabled title="Unir a los tres llega con la conferencia (fase 2)">
              <Icon name="conf" /> Unir a los tres
            </button>
            <button className="i-btnw" onClick={() => phone.cancelConsult()}>
              Volver con {c.contacto ? nombre.split(' ')[0] : 'el cliente'}
            </button>
          </div>
        </>
      );
    } else {
      mid = (
        <>
          {compact}
          {back('Transferir')}
          <input className="i-field mono" value={destino} onChange={(e) => setDestino(e.target.value)} placeholder="Extensión o número" autoComplete="off" aria-label="Extensión o número de destino" autoFocus onKeyDown={(e) => e.key === 'Enter' && destino && phone.transferBlind(destino)} />
          <div className="btn-col" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <button className="i-btnw acc" onClick={() => phone.transferBlind(destino)} disabled={!dialable(destino)}>
              Transferir ya
            </button>
            <button className="i-btnw" onClick={() => phone.startConsult(destino)} disabled={!dialable(destino)}>
              Consultar antes
            </button>
          </div>
          <button className="i-btnw" style={{ marginTop: 8 }} disabled title="Llega en la fase 2 del mapa 3CX">
            <Icon name="park" /> Estacionar la llamada
          </button>
          {puenteLlamada.data?.registrado ? (
            <div className="btn-col" style={{ gridTemplateColumns: '1fr auto', marginTop: 8 }}>
              <input className="i-field mono" value={caso} onChange={(e) => setCaso(e.target.value)} placeholder="Número del caso" aria-label="Número del caso" onKeyDown={(e) => e.key === 'Enter' && void pegarCaso()} />
              <button className="i-btnw" onClick={() => void pegarCaso()} disabled={!caso.trim()}>
                {casoPegado && casoPegado === caso.trim() ? 'Caso pegado ✓' : 'Pegar caso'}
              </button>
            </div>
          ) : null}
          <p className="i-note">“Transferir ya” pasa la llamada sin avisar. “Consultar antes” deja al cliente en espera mientras hablas con tu compañero, y luego completas o vuelves. “Estacionar” (para que otro la tome desde cualquier teléfono) llega en la fase 2.</p>
          <p className="i-note">El directorio del equipo con su estado (para elegir a quién pasar) llega con la integración XAPI.</p>
        </>
      );
    }
  } else if (c.vista === 'conf') {
    mid = (
      <>
        {compact}
        {back('Conferencia')}
        <div className="i-sec">En la llamada</div>
        <div className="i-row">
          <span className="idot live" />
          <div className="rt">
            <b>Tú</b>
            <span>{s.config.extension ? `Ext ${s.config.extension}` : 'Consola'}</span>
          </div>
        </div>
        <div className="i-row">
          <span className="idot live" />
          <div className="rt">
            <b>{nombre}</b>
            <span>{empresa}</span>
          </div>
        </div>
        <p className="i-note">Sumar a una tercera persona necesita el puente de conferencias en la pasarela de voz (fase 2 del mapa 3CX). Mientras tanto, usa “Transferir → Consultar antes”.</p>
      </>
    );
  } else if (c.vista === 'tip') {
    mid = (
      <>
        {compact}
        {back('Tipificar ahora')}
        <div className="i-chips">
          {(tips.data ?? []).map((t) => (
            <button key={t.id} className="i-chip" aria-pressed={c.tipificacionId === t.id} onClick={() => phone.setTipificacion(c.tipificacionId === t.id ? null : t.id)}>
              {t.nombre}
            </button>
          ))}
        </div>
        <p className="i-note">Se guarda sola al colgar. {tipRequiere ? 'Se creará una tarea de seguimiento para mañana.' : ''}</p>
      </>
    );
  } else if (c.vista === 'notes') {
    mid = (
      <>
        {compact}
        {back('Notas de la llamada')}
        <textarea className="i-field" style={{ height: 180, padding: 12, resize: 'none', lineHeight: 1.45 }} value={c.notas} onChange={(e) => phone.setNotas(e.target.value)} placeholder="Escribe mientras hablas. Se guarda en la llamada al colgar." autoFocus />
      </>
    );
  }

  return (
    <div className="cw">
      <div className="cw-top">
        <button className="i-ib" onClick={() => phone.collapse()} aria-label="Minimizar y seguir navegando">
          <Icon name="chevDown" />
        </button>
        <span className="call-st">
          {c.espera ? <span className="idot" style={{ ['--c' as string]: 'var(--amber)' }} /> : act ? <span className="idot live" /> : null}
          {statusTxt}
        </span>
        <button className="i-ib" onClick={() => c.contacto && onFicha(c.contacto.id)} aria-label="Abrir ficha" disabled={!c.contacto}>
          <Icon name="user" />
        </button>
      </div>
      <div className="cw-mid">
        {s.entrante2 ? (
          <div className="wait-banner" role="alert">
            <span className="idot live" />
            <div className="rt">
              <b>{s.entrante2.contacto?.nombre || formatPhone(s.entrante2.numero)}</b>
              <span>Llamada en espera</span>
            </div>
            <button style={{ background: 'var(--red)' }} onClick={() => phone.rejectWaiting()}>
              Rechazar
            </button>
            <button style={{ background: 'var(--green)' }} onClick={() => phone.answerWaiting()}>
              Contestar
            </button>
          </div>
        ) : null}
        {s.segunda ? (
          <div className="held-banner">
            <span className="idot" style={{ ['--c' as string]: 'var(--amber)' }} />
            <div className="rt">
              <b>{s.segunda.contacto?.nombre || formatPhone(s.segunda.numero)}</b> en espera
            </div>
            <button className="i-link" onClick={() => phone.swap()}>
              <Icon name="swap" /> Cambiar
            </button>
          </div>
        ) : null}
        {mid}
      </div>
      <div className="end-row">
        <button className="callbtn endbtn" onClick={() => phone.hangup()} aria-label="Colgar" data-testid="colgar">
          <PhoneFilled size={26} hang />
        </button>
      </div>
    </div>
  );
}
